/**
 * Vincent #132 (Eric 2026-05-16) — Page admin Backfill + Migrations.
 *
 * 2 sections :
 * 1. Backfill rétroactif : régénère Q&R + Procédures + Scripts pour TOUTES
 *    les vidéos `pipeline_status=complete` de l'org. Coûteux en crédits IA.
 * 2. Migrations DB : liste + bouton re-run pour les migrations Mongo.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import {
  Database, Warning, SpinnerGap, CheckCircle, ArrowsClockwise, ClockCounterClockwise,
  Lightning, ArrowLeft,
} from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";

export default function AdminMigrationsPage() {
  const { lang } = useLang();

  // Backfill state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  // Migrations state
  const [migrations, setMigrations] = useState([]);
  const [migLoading, setMigLoading] = useState(true);
  const [migRunning, setMigRunning] = useState(null);

  // Vincent B733 v3 (Eric 2026-02-14) — Bulk réapplication B733 sur toute l'org
  const [bulkB733Job, setBulkB733Job] = useState(null);
  const [bulkB733Starting, setBulkB733Starting] = useState(false);
  const bulkB733PollRef = useRef(null);

  // Vincent B733 v5 (Eric 2026-02-14) — Historique des 5 derniers bulk jobs
  const [bulkB733History, setBulkB733History] = useState([]);
  const loadBulkB733History = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/admin/videos/reapply-micro-turns-all/history?limit=5`,
        { withCredentials: true },
      );
      setBulkB733History(data?.jobs || []);
    } catch (e) {
      // silent
    }
  };

  const loadBulkB733Status = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/admin/videos/reapply-micro-turns-all/status`,
        { withCredentials: true },
      );
      if (data && data.status !== "none") {
        setBulkB733Job(data);
        if (data.status === "running" && !bulkB733PollRef.current) {
          bulkB733PollRef.current = setInterval(loadBulkB733Status, 4000);
        }
        // Vincent B733 v4 — Stoppe le polling aussi sur "cancelled" (job fini)
        if ((data.status === "complete" || data.status === "cancelled") && bulkB733PollRef.current) {
          clearInterval(bulkB733PollRef.current);
          bulkB733PollRef.current = null;
          // Vincent B733 v5 — Refresh l'historique quand un job vient de finir
          loadBulkB733History();
        }
      }
    } catch (e) {
      // silent
    }
  };

  const startBulkB733 = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Réappliquer la diarisation micro-tours (B733) à TOUTES les vidéos transcrites de cette organisation ?\n\n• Ne refait PAS Whisper — juste la diarisation AssemblyAI (~25 s par vidéo).\n• Traitement séquentiel pour éviter de saturer AssemblyAI.\n• Whisper, Fireflies, synthèse et Q&R sont CONSERVÉS.\n• Coût : 1 appel AssemblyAI BYOK par vidéo.\n\nContinuer ?"
        : "Re-apply B733 micro-turn diarization to ALL transcribed videos in this org? ~25s per video, sequential, Whisper/synth preserved.",
    );
    if (!ok) return;
    setBulkB733Starting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/videos/reapply-micro-turns-all`,
        {},
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `Bulk B733 lancé sur ${data.total} vidéo(s)`
          : `Bulk B733 started on ${data.total} video(s)`,
      );
      await loadBulkB733Status();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBulkB733Starting(false);
    }
  };

  // Vincent B733 v4 (Eric 2026-02-14) — Annuler le bulk en cours.
  // Bouton visible uniquement quand `bulkB733Job.status === "running"`.
  const [bulkB733Cancelling, setBulkB733Cancelling] = useState(false);
  const cancelBulkB733 = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Arrêter le bulk B733 en cours ?\n\nLa vidéo actuellement en traitement va se terminer normalement, puis le batch s'arrêtera. Les vidéos déjà traitées gardent leurs corrections.\n\nContinuer l'annulation ?"
        : "Cancel the running B733 bulk? The current video will finish, then the batch stops. Already-processed videos keep their fixes.",
    );
    if (!ok) return;
    setBulkB733Cancelling(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/videos/reapply-micro-turns-all/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `Annulation demandée. En cours : ${data.current_video_title || "—"}`
          : `Cancel requested. Current: ${data.current_video_title || "—"}`,
      );
      await loadBulkB733Status();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBulkB733Cancelling(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/backfill/all-knowledge/preview`, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'aperçu");
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadMigrations = async () => {
    setMigLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/migrations`, { withCredentials: true });
      setMigrations(data.items || []);
    } catch (e) {
      console.error("migrations load:", e);
    } finally {
      setMigLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
    loadMigrations();
    loadBulkB733Status();
    loadBulkB733History();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (bulkB733PollRef.current) clearInterval(bulkB733PollRef.current);
    };
  }, []);

  const startBackfill = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? `Re-générer Q&R + Procédures + Scripts pour ${preview?.total_videos} vidéos ?\n\nCoût estimé : ~$${preview?.estimated_cost_cad} CAD facturé à votre clé BYOK OpenAI.\nDurée : ~${preview?.estimated_duration_minutes} min.\n\nCette opération est IRRÉVERSIBLE (créera des doublons en pending_review).`
        : `Re-generate Q&A + Procedures + Scripts for ${preview?.total_videos} videos? Estimated cost: ~$${preview?.estimated_cost_cad} CAD.`
    );
    if (!ok) return;
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/backfill/all-knowledge/start`, {}, { withCredentials: true });
      setJob({ job_id: data.job_id, total: data.total_videos, completed: 0, percent: 0, status: "running" });
      toast.success(lang === "fr" ? `Backfill lancé sur ${data.total_videos} vidéos` : `Backfill started on ${data.total_videos} videos`);
      // Poll status every 5s
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/api/admin/backfill/jobs/${data.job_id}`, { withCredentials: true });
          setJob(st);
          if (st.status === "complete" || st.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (st.status === "complete") {
              toast.success(lang === "fr"
                ? `Backfill terminé : ${st.completed}/${st.total} vidéos`
                : `Backfill done: ${st.completed}/${st.total} videos`);
            } else {
              toast.error(st.error || "Backfill error");
            }
          }
        } catch (e) {
          console.error("poll:", e);
        }
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setStarting(false);
    }
  };

  const runMigration = async (id) => {
    const ok = await confirmDialog(lang === "fr" ? `Relancer la migration ${id} ?` : `Re-run migration ${id}?`);
    if (!ok) return;
    setMigRunning(id);
    try {
      const { data } = await axios.post(`${API}/api/admin/migrations/${id}/run`, {}, { withCredentials: true });
      toast.success(`${id} ${data.status}`);
      await loadMigrations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setMigRunning(null);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-migrations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database size={26} weight="duotone" className="text-[#002FA7]" />
            {lang === "fr" ? "Migrations & Backfill" : "Migrations & Backfill"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "fr"
              ? "Régénérer rétroactivement les connaissances (Q&R, Procédures, Scripts) pour toutes vos vidéos existantes."
              : "Retroactively regenerate knowledge (Q&A, Procedures, Scripts) for all existing videos."}
          </p>
        </div>
        <Link to="/dashboard" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid="back-link">
          <ArrowLeft size={12} /> {lang === "fr" ? "Tableau de bord" : "Dashboard"}
        </Link>
      </div>

      {/* ── Backfill section ─────────────────────────────────── */}
      <div className="border-2 border-amber-300 bg-amber-50/50 rounded-xl p-5" data-testid="backfill-section">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={20} weight="duotone" className="text-amber-600" />
          <h2 className="text-lg font-bold text-amber-900">
            {lang === "fr" ? "Backfill rétroactif complet" : "Full retroactive backfill"}
          </h2>
        </div>

        {previewLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="backfill-loading">
            <SpinnerGap size={16} className="animate-spin" /> {lang === "fr" ? "Préchargement…" : "Loading preview…"}
          </div>
        ) : preview ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-lg border border-amber-200 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">{lang === "fr" ? "Vidéos" : "Videos"}</div>
                <div className="text-2xl font-bold text-amber-900" data-testid="backfill-total-videos">{preview.total_videos}</div>
              </div>
              <div className="bg-white rounded-lg border border-amber-200 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">{lang === "fr" ? "Coût estimé" : "Est. cost"}</div>
                <div className="text-2xl font-bold text-amber-900" data-testid="backfill-cost">${preview.estimated_cost_cad}</div>
                <div className="text-[9px] text-amber-700">CAD via BYOK</div>
              </div>
              <div className="bg-white rounded-lg border border-amber-200 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">{lang === "fr" ? "Durée" : "Duration"}</div>
                <div className="text-2xl font-bold text-amber-900" data-testid="backfill-duration">~{preview.estimated_duration_minutes} min</div>
              </div>
            </div>
            <div className="text-xs bg-amber-100 text-amber-900 rounded p-2 mb-4 flex items-start gap-2">
              <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
              <span>{preview.warning}</span>
            </div>

            {!job ? (
              <button
                onClick={startBackfill}
                disabled={starting || preview.total_videos === 0}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="backfill-start-btn"
              >
                {starting ? <><SpinnerGap size={14} className="animate-spin" /> {lang === "fr" ? "Lancement…" : "Starting…"}</> : <><Lightning size={14} weight="bold" /> {lang === "fr" ? `Lancer le backfill sur ${preview.total_videos} vidéos` : `Backfill ${preview.total_videos} videos`}</>}
              </button>
            ) : (
              <div className="bg-white rounded-lg border border-amber-200 p-3" data-testid="backfill-job-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold inline-flex items-center gap-2">
                    {job.status === "complete" ? <CheckCircle size={14} weight="fill" className="text-teal-600" /> :
                     job.status === "error" ? <Warning size={14} weight="fill" className="text-rose-600" /> :
                     <SpinnerGap size={14} className="animate-spin text-amber-600" />}
                    {lang === "fr" ? `Job ${job.job_id?.slice(0,8)}` : `Job ${job.job_id?.slice(0,8)}`}
                  </span>
                  <span className="text-xs font-mono">{job.completed}/{job.total} ({job.percent}%)</span>
                </div>
                <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden mb-2">
                  <div className={`h-full transition-all duration-500 ${job.status === "complete" ? "bg-teal-500" : job.status === "error" ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${job.percent}%` }} />
                </div>
                {job.status === "complete" && (
                  <button onClick={() => setJob(null)} className="text-[11px] text-amber-700 hover:underline" data-testid="backfill-dismiss-btn">
                    {lang === "fr" ? "Fermer" : "Dismiss"}
                  </button>
                )}
                {job.error && <p className="text-[11px] text-rose-700 mt-1">{job.error}</p>}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Vincent B733 v3 (Eric 2026-02-14) — Bulk réapplication B733.
          Placé entre Backfill (coûteux GPT) et Migrations (DDL) car c'est
          une ré-analyse audio non-destructive (préserve Whisper + synthèse). */}
      <div className="border-2 border-emerald-300 bg-emerald-50/50 rounded-xl p-5" data-testid="bulk-b733-section">
        <div className="flex items-center gap-2 mb-3">
          <ArrowsClockwise size={20} weight="duotone" className="text-emerald-600" />
          <h2 className="text-lg font-bold text-emerald-900">
            {lang === "fr" ? "Réappliquer B733 (micro-tours) à toutes les vidéos" : "Bulk re-apply B733 micro-turns"}
          </h2>
        </div>
        <p className="text-xs text-emerald-900/80 mb-3">
          {lang === "fr"
            ? "Corrige la distribution des locuteurs sur les vidéos anciennes (ex: 95/4 → 60/40). Traite les vidéos une par une (~25 s chacune). Ne refait PAS Whisper — Whisper/Fireflies/synthèse/Q&R sont conservés."
            : "Fixes speaker distribution on older videos. Sequential (~25s each). Whisper/synth preserved."}
        </p>
        {!bulkB733Job || bulkB733Job.status === "none" ? (
          <button
            onClick={startBulkB733}
            disabled={bulkB733Starting}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="bulk-b733-start-btn"
          >
            {bulkB733Starting ? (
              <><SpinnerGap size={14} className="animate-spin" /> {lang === "fr" ? "Lancement…" : "Starting…"}</>
            ) : (
              <><ArrowsClockwise size={14} weight="bold" /> {lang === "fr" ? "Lancer sur toutes les vidéos" : "Run on all videos"}</>
            )}
          </button>
        ) : (
          <div className="bg-white rounded-lg border border-emerald-200 p-3" data-testid="bulk-b733-job-panel">
            <div className="flex items-center justify-between mb-2 gap-2">
              <span className="text-sm font-semibold inline-flex items-center gap-2">
                {bulkB733Job.status === "complete" ? <CheckCircle size={14} weight="fill" className="text-teal-600" /> :
                 bulkB733Job.status === "cancelled" ? <Warning size={14} weight="fill" className="text-orange-600" /> :
                 <SpinnerGap size={14} className="animate-spin text-emerald-600" />}
                {lang === "fr" ? `Job ${bulkB733Job.id?.slice(0,8)}` : `Job ${bulkB733Job.id?.slice(0,8)}`}
                {bulkB733Job.cancel_requested && bulkB733Job.status === "running" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-semibold" data-testid="bulk-b733-cancel-pending">
                    {lang === "fr" ? "Annulation demandée" : "Cancel requested"}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono" data-testid="bulk-b733-progress-counter">
                  {bulkB733Job.done || 0}/{bulkB733Job.total || 0} ({bulkB733Job.percent || 0}%)
                </span>
                {/* Vincent B733 v4 — Bouton Arrêter visible pendant running
                    (sauf si déjà cancel_requested → évite le double-clic). */}
                {bulkB733Job.status === "running" && !bulkB733Job.cancel_requested && (
                  <button
                    onClick={cancelBulkB733}
                    disabled={bulkB733Cancelling}
                    className="text-[11px] px-2 py-1 rounded bg-white border border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="bulk-b733-cancel-btn"
                    title={lang === "fr" ? "Arrêter le bulk après la vidéo en cours" : "Stop the bulk after the current video"}
                  >
                    {bulkB733Cancelling ? <SpinnerGap size={11} className="animate-spin" /> : "⏹"}
                    {lang === "fr" ? "Arrêter" : "Stop"}
                  </button>
                )}
              </div>
            </div>
            <div className="w-full h-2 bg-emerald-100 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full transition-all duration-500 ${
                  bulkB733Job.status === "complete" ? "bg-teal-500" :
                  bulkB733Job.status === "cancelled" ? "bg-orange-500" :
                  "bg-emerald-500"
                }`}
                style={{ width: `${bulkB733Job.percent || 0}%` }}
                data-testid="bulk-b733-progress-bar"
              />
            </div>
            {bulkB733Job.current_video_title && bulkB733Job.status === "running" && (
              <div className="text-[11px] text-emerald-800 truncate" data-testid="bulk-b733-current-video">
                {lang === "fr" ? "En cours : " : "Current: "}
                <em>{bulkB733Job.current_video_title}</em>
              </div>
            )}
            {(bulkB733Job.status === "complete" || bulkB733Job.status === "cancelled") && (
              <div className="text-[11px] text-emerald-800 flex items-center justify-between gap-3">
                <span data-testid="bulk-b733-summary">
                  {bulkB733Job.status === "cancelled" && (
                    <span className="font-semibold text-orange-800" data-testid="bulk-b733-cancelled-label">
                      {lang === "fr" ? `⏹ Annulé après ${bulkB733Job.done || 0}/${bulkB733Job.total || 0} — ` : `⏹ Cancelled after ${bulkB733Job.done || 0}/${bulkB733Job.total || 0} — `}
                    </span>
                  )}
                  {(bulkB733Job.results?.length || 0)}{" "}
                  {lang === "fr" ? "réussie(s)" : "success"}
                  {bulkB733Job.errors?.length > 0 && (
                    <>, <span className="text-rose-700 font-semibold">{bulkB733Job.errors.length}{" "}{lang === "fr" ? "échec(s)" : "error(s)"}</span></>
                  )}
                </span>
                <button
                  onClick={() => setBulkB733Job(null)}
                  className="text-[11px] text-emerald-700 hover:underline"
                  data-testid="bulk-b733-dismiss-btn"
                >
                  {lang === "fr" ? "Fermer" : "Dismiss"}
                </button>
              </div>
            )}
            {bulkB733Job.errors?.length > 0 && (bulkB733Job.status === "complete" || bulkB733Job.status === "cancelled") && (
              <details className="mt-2 text-[10px]" data-testid="bulk-b733-errors-details">
                <summary className="cursor-pointer text-rose-700 font-semibold">
                  {lang === "fr" ? `Détails erreurs (${bulkB733Job.errors.length})` : `Errors (${bulkB733Job.errors.length})`}
                </summary>
                <ul className="mt-1 space-y-0.5">
                  {bulkB733Job.errors.slice(0, 20).map((err, i) => (
                    <li key={i} className="text-rose-700 truncate">
                      • <span className="font-mono">{err.video_id?.slice(0,8)}</span> {err.title} — {err.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* Vincent B733 v5 (Eric 2026-02-14) — Historique des 5 derniers bulk
            jobs pour audit rapide (qui / quand / résultat). Section pliable
            par défaut pour ne pas polluer l'UI. */}
        {bulkB733History.length > 0 && (
          <details className="mt-3 text-[11px]" data-testid="bulk-b733-history">
            <summary className="cursor-pointer text-emerald-900 font-semibold inline-flex items-center gap-1.5">
              <ClockCounterClockwise size={12} weight="bold" />
              {lang === "fr"
                ? `Historique (${bulkB733History.length} dernier(s) job(s))`
                : `History (last ${bulkB733History.length} job(s))`}
            </summary>
            <div className="mt-2 bg-white rounded-lg border border-emerald-200 overflow-hidden">
              <table className="w-full text-[10px]" data-testid="bulk-b733-history-table">
                <thead className="bg-emerald-50 text-emerald-900 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Date</th>
                    <th className="px-2 py-1.5 text-left">{lang === "fr" ? "Lancé par" : "By"}</th>
                    <th className="px-2 py-1.5 text-center">{lang === "fr" ? "Statut" : "Status"}</th>
                    <th className="px-2 py-1.5 text-center">{lang === "fr" ? "Vidéos" : "Videos"}</th>
                    <th className="px-2 py-1.5 text-center">✅</th>
                    <th className="px-2 py-1.5 text-center">❌</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100">
                  {bulkB733History.map((h) => {
                    const dateStr = h.started_at
                      ? new Date(h.started_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
                          year: "2-digit", month: "2-digit", day: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        })
                      : "—";
                    const statusIcon =
                      h.status === "complete" ? "✅" :
                      h.status === "cancelled" ? "⏹" :
                      h.status === "running" ? "⏳" : "?";
                    const statusColor =
                      h.status === "complete" ? "text-teal-700" :
                      h.status === "cancelled" ? "text-orange-700" :
                      h.status === "running" ? "text-emerald-700" : "text-zinc-500";
                    return (
                      <tr key={h.id} className="hover:bg-emerald-50/60" data-testid={`bulk-b733-history-row-${h.id?.slice(0,8)}`}>
                        <td className="px-2 py-1 font-mono text-emerald-900" title={h.started_at}>{dateStr}</td>
                        <td className="px-2 py-1 truncate max-w-[160px]" title={h.started_by_email}>
                          {h.started_by_email || h.started_by?.slice(0,8) || "—"}
                        </td>
                        <td className={`px-2 py-1 text-center font-semibold ${statusColor}`}>{statusIcon} {h.status}</td>
                        <td className="px-2 py-1 text-center font-mono">{h.done}/{h.total}</td>
                        <td className="px-2 py-1 text-center text-teal-700 font-semibold">{h.success_count}</td>
                        <td className="px-2 py-1 text-center text-rose-700 font-semibold">{h.error_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>

      {/* ── Migrations section ───────────────────────────────── */}
      <div className="border border-border bg-card rounded-xl p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-lg font-bold inline-flex items-center gap-2">
            <ArrowsClockwise size={20} weight="duotone" className="text-blue-600" />
            {lang === "fr" ? "Migrations base de données" : "Database migrations"}
          </h2>
          <button onClick={loadMigrations} className="text-xs text-muted-foreground hover:text-zinc-700" data-testid="migrations-refresh-btn">
            {lang === "fr" ? "↻ Actualiser" : "↻ Refresh"}
          </button>
        </div>

        {migLoading ? (
          <div className="text-sm text-muted-foreground">Chargement…</div>
        ) : migrations.length === 0 ? (
          <div className="text-sm text-muted-foreground">{lang === "fr" ? "Aucune migration enregistrée" : "No migrations registered"}</div>
        ) : (
          <div className="divide-y divide-border">
            {migrations.map((m) => {
              const lastStatus = m.last_run?.status;
              return (
                <div key={m.id} className="py-2 flex items-center gap-3" data-testid={`migration-row-${m.id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    lastStatus === "success" ? "bg-teal-500" :
                    lastStatus === "error" ? "bg-rose-500" : "bg-zinc-300"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{m.id}</div>
                    <div className="text-[10px] text-muted-foreground">{m.description}</div>
                  </div>
                  <div className="text-[10px] text-muted-foreground hidden sm:block min-w-[160px] text-right">
                    {m.last_run?.finished_at ? new Date(m.last_run.finished_at).toLocaleString("fr-CA") : <em>Jamais</em>}
                  </div>
                  <button
                    onClick={() => runMigration(m.id)}
                    disabled={migRunning === m.id}
                    className="px-2.5 py-1 text-[10px] rounded bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 disabled:opacity-50"
                    data-testid={`migration-run-${m.id}`}
                  >
                    {migRunning === m.id ? <SpinnerGap size={10} className="animate-spin" /> : (lang === "fr" ? "Relancer" : "Re-run")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Vincent B760 (Eric 2026-02-17) — Backfill Fireflies S3 */}
      <FirefliesBackfillPanel lang={lang} />

      {/* Vincent B763 (Eric 2026-02-17) — Défauts fusion Org */}
      <OrgFusionDefaultsPanel lang={lang} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Vincent B763 — Panel défauts fusion au niveau ORG
// ─────────────────────────────────────────────────────────
function OrgFusionDefaultsPanel({ lang }) {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(["whisper", "fireflies"]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/org/fusion-defaults`,
        { withCredentials: true },
      );
      setCurrent(data);
      if (Array.isArray(data.fusion_sources_default) && data.fusion_sources_default.length > 0) {
        setSel(data.fusion_sources_default);
      }
    } catch (e) { /* silent */ } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (payloadSources) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/org/fusion-defaults`,
        { sources: payloadSources },
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? "Défaut org enregistré." : "Org default saved.");
      await load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4" data-testid="admin-org-fusion-defaults">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
            ⚙️ {lang === "fr" ? "Défauts fusion — Organisation" : "Fusion defaults — Organization"}
          </h2>
          <p className="text-xs text-indigo-800/80 mt-1">
            {lang === "fr"
              ? "Défaut appliqué quand un concepteur n'a pas encore défini sa propre préférence. Priorité : perso > org > ["
              : "Fallback default when a designer has no personal preference. Priority: personal > org > ["}
            <span className="font-mono">whisper, fireflies</span>].
          </p>
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">
        {lang === "fr" ? "État actuel" : "Current"}
      </div>
      <div className="bg-white/60 rounded p-2 mb-3 text-xs">
        {loading ? (
          <span className="text-zinc-500 italic">{lang === "fr" ? "Chargement…" : "Loading…"}</span>
        ) : current?.fusion_sources_default ? (
          <span data-testid="org-fusion-defaults-current">
            {current.fusion_sources_default.join(" + ")}
            <span className="text-zinc-500 ml-2">
              ({new Date(current.updated_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")})
            </span>
          </span>
        ) : (
          <span className="text-zinc-500 italic" data-testid="org-fusion-defaults-empty">
            {lang === "fr" ? "Aucun défaut org — fallback [whisper, fireflies]" : "No org default — fallback [whisper, fireflies]"}
          </span>
        )}
      </div>

      <div className="space-y-1 mb-3">
        {["whisper", "fireflies"].map((k) => (
          <label
            key={k}
            className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${
              sel.includes(k) ? "border-indigo-400 bg-indigo-100/50" : "border-zinc-200 bg-white"
            }`}
          >
            <input
              type="checkbox"
              checked={sel.includes(k)}
              onChange={(e) => {
                setSel((prev) => e.target.checked
                  ? [...new Set([...prev, k])]
                  : prev.filter((s) => s !== k));
              }}
              className="h-4 w-4 accent-indigo-600"
              data-testid={`org-fusion-checkbox-${k}`}
            />
            <span className="text-sm font-medium text-zinc-800 capitalize">{k}</span>
          </label>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => save(sel)}
          disabled={saving || sel.length === 0}
          className="text-[11px] font-semibold px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          data-testid="org-fusion-defaults-save"
        >
          {saving ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
          {lang === "fr" ? "Enregistrer" : "Save"}
        </button>
        <button
          onClick={() => save(null)}
          disabled={saving || !current?.fusion_sources_default}
          className="text-[11px] font-semibold px-3 py-1 rounded bg-white border border-red-300 text-red-800 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="org-fusion-defaults-clear"
          title={lang === "fr" ? "Supprime le défaut org (revient au fallback)" : "Removes the org default (reverts to fallback)"}
        >
          <Warning size={12} weight="bold" />
          {lang === "fr" ? "Effacer" : "Clear"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Vincent B760 — Panel dédié Backfill Fireflies S3
// ─────────────────────────────────────────────────────────
function FirefliesBackfillPanel({ lang }) {
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);

  const loadLast = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/fireflies-backfill/last`,
        { withCredentials: true },
      );
      setLastRun(data.last_run || null);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadLast(); }, []);

  const triggerBackfill = async (apply, force) => {
    if (apply) {
      const ok = await confirmDialog(
        lang === "fr"
          ? `Lancer le backfill Fireflies EN RÉEL${force ? " (mode --force = re-parse aussi les segments non vides)" : ""} ?\n\nCette action va parcourir toutes les vidéos avec fireflies_storage_path, télécharger le DOCX depuis S3, parser, et insérer/mettre à jour secondary_transcripts. Coûte du temps réseau (S3) mais pas de crédits IA.`
          : `Run Fireflies backfill FOR REAL${force ? " (--force also re-parses non-empty segments)" : ""}?`,
        { confirmLabel: lang === "fr" ? "Lancer" : "Run", tone: "warning" },
      );
      if (!ok) return;
    }
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/fireflies-backfill/run?apply=${apply}&force=${force}`,
        {},
        { withCredentials: true, timeout: 5 * 60 * 1000 },
      );
      if (apply) {
        toast.success(
          lang === "fr"
            ? `Backfill terminé : ${data.stats.inserted} nouveaux + ${data.stats.reparsed} re-parsed`
            : `Backfill done: ${data.stats.inserted} new + ${data.stats.reparsed} reparsed`,
        );
        await loadLast();
        setDryRunResult(null);
      } else {
        setDryRunResult(data);
      }
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const renderStats = (stats) => (
    <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
      {Object.entries(stats).map(([k, v]) => (
        <div key={k} className="bg-white border border-zinc-200 rounded px-2 py-1">
          <div className="text-zinc-500 uppercase text-[9px]">{k.replace(/_/g, " ")}</div>
          <div className="text-zinc-900 font-semibold">{v}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="admin-fireflies-backfill">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
            🔥 {lang === "fr" ? "Backfill Fireflies S3" : "Fireflies S3 Backfill"}
          </h2>
          <p className="text-xs text-orange-800/80 mt-1">
            {lang === "fr"
              ? "Re-parse et insère les transcriptions Fireflies stockées dans S3 mais absentes de secondary_transcripts (ou avec segments vides). Idempotent, safe à lancer plusieurs fois."
              : "Re-parses and inserts Fireflies transcripts stored in S3 but missing in secondary_transcripts."}
          </p>
        </div>
        <button
          onClick={loadLast}
          disabled={loading}
          className="flex-shrink-0 text-[11px] font-semibold px-3 py-1 rounded bg-white border border-orange-300 text-orange-800 hover:bg-orange-100 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="fireflies-backfill-refresh"
        >
          {loading ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
          {lang === "fr" ? "Actualiser" : "Refresh"}
        </button>
      </div>

      {/* Dernière exécution */}
      <div className="bg-white/50 rounded p-3 mb-3">
        <div className="text-[10px] uppercase tracking-wide text-orange-700 font-semibold mb-1">
          {lang === "fr" ? "Dernière exécution" : "Last run"}
        </div>
        {lastRun ? (
          <div data-testid="fireflies-backfill-last-run">
            <div className="text-xs text-zinc-700">
              {new Date(lastRun.completed_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
              {" — "}
              <span className="text-zinc-500">
                {lastRun.triggered_by_email || lastRun.triggered_by || "?"}
              </span>
              {lastRun.force && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9px] font-semibold">FORCE</span>
              )}
            </div>
            {renderStats(lastRun.stats || {})}
            {(lastRun.errors_sample || []).length > 0 && (
              <details className="mt-2 text-[11px] text-red-700">
                <summary className="cursor-pointer font-semibold">
                  {lang === "fr" ? "Erreurs" : "Errors"} ({lastRun.errors_sample.length})
                </summary>
                <ul className="ml-4 mt-1 space-y-0.5">
                  {lastRun.errors_sample.slice(0, 5).map((e, i) => (
                    <li key={i} className="truncate">
                      {e.video_id?.slice(0, 8)} — {e.title} : {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : (
          <div className="text-xs text-zinc-500 italic">
            {lang === "fr" ? "Aucune exécution enregistrée sur cette org." : "No run recorded for this org."}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-2">
        <button
          onClick={() => triggerBackfill(false, false)}
          disabled={running}
          className="text-[11px] font-semibold px-3 py-1 rounded bg-white border border-orange-300 text-orange-800 hover:bg-orange-100 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="fireflies-backfill-dry-run"
        >
          {running ? <SpinnerGap size={12} className="animate-spin" /> : <Lightning size={12} weight="bold" />}
          {lang === "fr" ? "Simuler (dry-run)" : "Simulate (dry-run)"}
        </button>
        <button
          onClick={() => triggerBackfill(true, false)}
          disabled={running}
          className="text-[11px] font-semibold px-3 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
          data-testid="fireflies-backfill-apply"
        >
          {running ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
          {lang === "fr" ? "Lancer" : "Run"}
        </button>
        <button
          onClick={() => triggerBackfill(true, true)}
          disabled={running}
          className="text-[11px] font-semibold px-3 py-1 rounded bg-white border border-red-300 text-red-800 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="fireflies-backfill-apply-force"
          title={lang === "fr" ? "Re-parse aussi les vidéos avec segments non vides" : "Also re-parse videos with non-empty segments"}
        >
          {running ? <SpinnerGap size={12} className="animate-spin" /> : <Warning size={12} weight="bold" />}
          {lang === "fr" ? "Lancer --force" : "Run --force"}
        </button>
      </div>

      {/* Dry-run result panel */}
      {dryRunResult && (
        <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mt-2" data-testid="fireflies-backfill-dry-run-result">
          <div className="text-[10px] uppercase tracking-wide text-yellow-800 font-semibold mb-1">
            {lang === "fr" ? "Simulation (aucune écriture)" : "Simulation (no write)"}
          </div>
          {renderStats(dryRunResult.stats || {})}
        </div>
      )}
    </div>
  );
}
